import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

/**
 * 检查用户是否需要完成个性化设置
 * 注意：这个函数现在是异步的，因为需要查询数据库
 * @param user Supabase 用户对象
 * @returns 是否需要完成个性化设置
 */
export const needsPersonaOnboarding = async (user: User | null): Promise<boolean> => {
  if (!user) return false;

  try {
    // 从 profiles 表查询 onboarding 状态
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('onboarding_completed, persona')
      .eq('id', user.id)
      .single();

    if (error) {
      console.error('[OnboardingUtils] Error checking onboarding status:', error);
      // 如果查询失败，默认需要 onboarding
      return true;
    }

    // 如果已经完成 onboarding，则不需要（无论是填写了还是跳过了）
    if (profile?.onboarding_completed) {
      return false;
    }

    // 如果有 persona 数据但没有 onboarding_completed 标记，也认为已完成
    // 这是为了兼容旧数据
    if (profile?.persona) {
      return false;
    }

    // 否则需要 onboarding
    return true;
  } catch (err) {
    console.error('[OnboardingUtils] Error in needsPersonaOnboarding:', err);
    // 出错时默认需要 onboarding
    return true;
  }
};

// 注意：之前的同步版本 needsPersonaOnboardingSync 已被移除
// 现在统一使用异步版本 needsPersonaOnboarding，因为数据存储在 profiles 表中

/**
 * 检查用户是否是新注册的用户（基于创建时间）
 * @param user Supabase 用户对象
 * @returns 是否是新用户（注册时间在 5 分钟内）
 */
export const isNewUser = (user: User | null): boolean => {
  if (!user || !user.created_at) return false;

  const createdAt = new Date(user.created_at);
  const now = new Date();
  const diffMinutes = (now.getTime() - createdAt.getTime()) / (1000 * 60);

  // 如果用户是在 5 分钟内创建的，认为是新用户
  return diffMinutes <= 5;
};

/**
 * 获取 onboarding 完成后应该跳转的路径
 * @param intendedPath 用户原本想要访问的路径
 * @returns 完成 onboarding 后的跳转路径
 */
export const getPostOnboardingPath = (intendedPath?: string | null): string => {
  // 如果有指定的路径，使用指定路径
  if (intendedPath && intendedPath !== '/v3/persona-onboarding') {
    return intendedPath;
  }

  // 默认跳转到主页
  return '/v3/taskv2';
};

/**
 * 设置 onboarding 重定向路径到 localStorage
 * @param path 要保存的路径
 */
export const setOnboardingRedirectPath = (path: string): void => {
  try {
    localStorage.setItem('onboarding-redirect-path', path);
  } catch (e) {
    console.warn('[OnboardingUtils] Failed to save redirect path:', e);
  }
};

/**
 * 获取并清除 onboarding 重定向路径
 * @returns 保存的重定向路径
 */
export const getAndClearOnboardingRedirectPath = (): string | null => {
  try {
    const path = localStorage.getItem('onboarding-redirect-path');
    if (path) {
      localStorage.removeItem('onboarding-redirect-path');
    }
    return path;
  } catch (e) {
    console.warn('[OnboardingUtils] Failed to get redirect path:', e);
    return null;
  }
};

/**
 * 检查当前路径是否应该跳过 onboarding 检查
 * @param pathname 当前路径
 * @returns 是否应该跳过 onboarding
 */
export const shouldSkipOnboardingCheck = (pathname: string): boolean => {
  const skipPaths = [
    '/v3/login',
    '/v3/invite-code',
    '/v3/persona-onboarding',
    '/v3/account-management'
  ];

  return skipPaths.some(path => pathname.startsWith(path));
};

/**
 * 标记用户已完成 onboarding（在 PersonaOnboarding 组件中调用）
 * 这个函数会在用户完成个性化设置时被调用
 */
export const markOnboardingCompleted = (): void => {
  // 这个函数主要是为了文档化目的
  // 实际的标记逻辑在 PersonaOnboarding 组件中通过更新用户元数据实现
  console.log('[OnboardingUtils] Onboarding marked as completed');
};
